/**
 * 文件上传管理 Composable
 * 支持图片（jpg/jpeg/png/gif/webp/svg）、PDF、文档（md/doc/docx）、表格（xls/xlsx）、演示文稿（ppt/pptx）文件的上传、预览和管理
 */

import { computed, onUnmounted, ref } from 'vue'
import { v4 as uuidv4 } from 'uuid'

// ==================== 类型定义 ====================

export interface UploadedFile {
  id: string
  file: File
  url: string // 本地预览 URL
  type: 'image' | 'pdf' | 'doc'
  name: string
  size: number
  mimeType: string
  previewUrl?: string // 图片预览 URL
  uploadStatus: 'pending' | 'uploading' | 'success' | 'error'
  uploadedUrl?: string // 服务器返回的 URL
}

export interface FileUploadConfig {
  maxImageSize: number // 图片最大大小（字节），默认 5MB
  maxPdfSize: number // PDF 最大大小（字节），默认 10MB
  maxDocSize: number // 文档最大大小（字节），默认 10MB
  maxFileCount: number // 最大文件数量，默认 5
  allowedImageTypes: string[] // 允许的图片类型
  allowedDocTypes: string[] // 允许的文档类型
}

// ==================== 常量配置 ====================

const DEFAULT_CONFIG: FileUploadConfig = {
  maxImageSize: 5 * 1024 * 1024, // 5MB
  maxPdfSize: 10 * 1024 * 1024, // 10MB
  maxDocSize: 10 * 1024 * 1024, // 10MB
  maxFileCount: 5,
  allowedImageTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'],
  allowedDocTypes: [
    'application/pdf',
    'text/markdown',
    'text/x-markdown',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation'
  ]
}

// ==================== Composable ====================

export function useFileUpload(config: Partial<FileUploadConfig> = {}) {
  const uploadConfig = { ...DEFAULT_CONFIG, ...config }
  
  // 已上传的文件列表
  const uploadedFiles = ref<UploadedFile[]>([])
  
  // 上传状态
  const isUploading = computed(() => 
    uploadedFiles.value.some(f => f.uploadStatus === 'uploading')
  )
  
  // 是否可以继续添加文件
  const canAddMore = computed(() => 
    uploadedFiles.value.length < uploadConfig.maxFileCount
  )

  /**
   * 验证文件大小
   */
  const validateFileSize = (file: File): { valid: boolean; message?: string } => {
    const isImage = uploadConfig.allowedImageTypes.includes(file.type)
    const isDoc = uploadConfig.allowedDocTypes.includes(file.type)

    if (!isImage && !isDoc) {
      return { valid: false, message: `不支持的文件格式: ${file.type}` }
    }

    if (isImage && file.size > uploadConfig.maxImageSize) {
      return {
        valid: false,
        message: `图片大小不能超过 ${uploadConfig.maxImageSize / 1024 / 1024}MB`
      }
    }

    if (isDoc && file.size > uploadConfig.maxDocSize) {
      return {
        valid: false,
        message: `文件大小不能超过 ${uploadConfig.maxDocSize / 1024 / 1024}MB`
      }
    }

    return { valid: true }
  }

  /**
   * 验证文件数量
   */
  const validateFileCount = (newCount: number): { valid: boolean; message?: string } => {
    if (uploadedFiles.value.length + newCount > uploadConfig.maxFileCount) {
      return { 
        valid: false, 
        message: `最多只能上传 ${uploadConfig.maxFileCount} 个文件` 
      }
    }
    return { valid: true }
  }

  /**
   * 创建本地预览 URL
   */
  const createPreviewUrl = (file: File): string => {
    return URL.createObjectURL(file)
  }

  /**
   * 添加文件到上传列表
   */
  const addFiles = (files: FileList | File[]): void => {
    const fileArray = Array.from(files)
    
    // 验证文件数量
    const countValidation = validateFileCount(fileArray.length)
    if (!countValidation.valid) {
      console.error(countValidation.message)
      alert(countValidation.message)
      return
    }
    
    // 逐个处理文件
    fileArray.forEach(file => {
      // 验证文件大小和类型
      const validation = validateFileSize(file)
      if (!validation.valid) {
        console.error(validation.message)
        alert(validation.message)
        return
      }
      
      // 判断文件类型
      const isImage = uploadConfig.allowedImageTypes.includes(file.type)
      const fileType = isImage ? 'image' : 'doc'

      // 创建文件对象
      const uploadedFile: UploadedFile = {
        id: uuidv4(),
        file,
        url: createPreviewUrl(file),
        type: fileType,
        name: file.name,
        size: file.size,
        mimeType: file.type,
        previewUrl: fileType === 'image' ? createPreviewUrl(file) : undefined,
        uploadStatus: 'pending'
      }
      
      uploadedFiles.value.push(uploadedFile)
    })
  }

  /**
   * 从输入框选择文件
   */
  const handleFileSelect = (event: Event): void => {
    const input = event.target as HTMLInputElement
    if (input.files && input.files.length > 0) {
      addFiles(input.files)
      // 清空 input，允许重复选择同一文件
      input.value = ''
    }
  }

  /**
   * 处理拖拽上传
   */
  const handleDrop = (event: DragEvent): void => {
    event.preventDefault()
    event.stopPropagation()
    
    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      addFiles(event.dataTransfer.files)
    }
  }

  /**
   * 处理拖拽经过
   */
  const handleDragOver = (event: DragEvent): void => {
    event.preventDefault()
    event.stopPropagation()
  }

  /**
   * 处理粘贴图片
   */
  const handlePaste = (event: ClipboardEvent): void => {
    const items = event.clipboardData?.items
    if (!items) return

    const files: File[] = []

    for (let i = 0; i < items.length; i++) {
      const item = items[i]

      // 处理图片
      if (item.kind === 'file' && uploadConfig.allowedImageTypes.includes(item.type)) {
        const file = item.getAsFile()
        if (file) {
          files.push(file)
        }
      }
    }

    if (files.length > 0) {
      addFiles(files)
    }
  }

  /**
   * 删除文件
   */
  const removeFile = (fileId: string): void => {
    const index = uploadedFiles.value.findIndex(f => f.id === fileId)
    if (index !== -1) {
      const file = uploadedFiles.value[index]
      
      // 释放 URL 对象
      if (file.url) {
        URL.revokeObjectURL(file.url)
      }
      if (file.previewUrl) {
        URL.revokeObjectURL(file.previewUrl)
      }
      
      uploadedFiles.value.splice(index, 1)
    }
  }

  /**
   * 清空所有文件
   */
  const clearAllFiles = (): void => {
    uploadedFiles.value.forEach(file => {
      if (file.url) {
        URL.revokeObjectURL(file.url)
      }
      if (file.previewUrl) {
        URL.revokeObjectURL(file.previewUrl)
      }
    })
    uploadedFiles.value = []
  }

  /**
   * 上传单个文件到服务器
   */
  const uploadFileToServer = async (uploadedFile: UploadedFile): Promise<string | null> => {
    try {
      uploadedFile.uploadStatus = 'uploading'
      
      const formData = new FormData()
      formData.append('file', uploadedFile.file)
      formData.append('type', uploadedFile.type)
      
      const response: any = await $fetch('/api/upload/file', {
        method: 'POST',
        body: formData
      })
      
      if (response.success && response.url) {
        uploadedFile.uploadStatus = 'success'
        uploadedFile.uploadedUrl = response.url
        return response.url
      } else {
        uploadedFile.uploadStatus = 'error'
        console.error('文件上传失败:', response.errorMessage)
        return null
      }
    } catch (error) {
      uploadedFile.uploadStatus = 'error'
      console.error('文件上传异常:', error)
      return null
    }
  }

  /**
   * 上传所有文件
   */
  const uploadAllFiles = async (): Promise<{ success: boolean; urls: string[] }> => {
    const pendingFiles = uploadedFiles.value.filter(f => f.uploadStatus === 'pending')
    
    if (pendingFiles.length === 0) {
      return { 
        success: true, 
        urls: uploadedFiles.value.map(f => f.uploadedUrl || '').filter(Boolean)
      }
    }
    
    const uploadPromises = pendingFiles.map(file => uploadFileToServer(file))
    const results = await Promise.all(uploadPromises)
    
    const urls = results.filter(Boolean) as string[]
    
    return {
      success: urls.length === pendingFiles.length,
      urls
    }
  }

  /**
   * 获取附件数据（用于发送到 AI）
   */
  const getAttachments = () => {
    return uploadedFiles.value.map(file => ({
      id: file.id,
      type: file.type,
      name: file.name,
      url: file.uploadedUrl || file.url,
      size: file.size,
      mimeType: file.mimeType
    }))
  }

  // ==================== 生命周期 ====================

  // 组件卸载时清理所有 URL 对象
  onUnmounted(() => {
    clearAllFiles()
  })

  return {
    // 状态
    uploadedFiles,
    isUploading,
    canAddMore,
    
    // 文件操作
    addFiles,
    handleFileSelect,
    handleDrop,
    handleDragOver,
    handlePaste,
    removeFile,
    clearAllFiles,
    
    // 上传操作
    uploadAllFiles,
    getAttachments
  }
}
